import { useEffect, useState } from 'react'

/**
 * The mobile breakpoint, as the design defines it.
 *
 * 760px, and taken from the prototype verbatim: `window.innerWidth < 760`. Not
 * a CSS media query, because the difference is not styling. Below this width
 * the interface is a different interface — a bottom tab bar instead of a header,
 * bottom sheets instead of tab rows, cards instead of tables, and four screens
 * that refuse to render at all. A media query can hide a table; it cannot stop
 * React from mounting one and reading its data.
 *
 * Width rather than a touch or user-agent test, deliberately. A narrow window on
 * a desktop gets the mobile layout, which is correct — the constraint being
 * solved for is horizontal space, and a half-width browser window has exactly
 * the same amount of it as a phone.
 */
const BREAKPOINT = 760

export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < BREAKPOINT,
  )

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < BREAKPOINT)
    // Once on mount as well as on resize: the initialiser above runs before the
    // first paint, but a device rotated between load and hydration would leave
    // the two disagreeing.
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  return isMobile
}
